import React, { useState, useEffect } from 'react';
import { boardsAPI, tasksAPI, User, Board } from '../api/api';
import './Modal.css';

interface EditBoardModalProps {
  board: Board;
  onClose: () => void;
  onBoardUpdated: () => void;
}

const EditBoardModal: React.FC<EditBoardModalProps> = ({ board, onClose, onBoardUpdated }) => {
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description || '');
  const [visibility, setVisibility] = useState<'public' | 'private'>(board.visibility || 'public');
  const [selectedMembers, setSelectedMembers] = useState<number[]>(
    board.members?.map(m => m.id) || []
  );
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const data = await tasksAPI.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Ошибка при загрузке пользователей:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Название доски обязательно');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await boardsAPI.update(board.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        visibility,
        member_ids: visibility === 'private' ? selectedMembers : undefined
      });
      onBoardUpdated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при обновлении доски');
    } finally {
      setLoading(false);
    }
  };

  const toggleMember = (userId: number) => {
    setSelectedMembers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.username.toLowerCase().includes(query) ||
      (user.email && user.email.toLowerCase().includes(query))
    );
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Редактировать доску</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название доски *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Например: Разработка сайта"
            />
          </div>
          <div className="form-group">
            <label>Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Описание проекта (необязательно)"
            />
          </div>
          <div className="form-group">
            <label>Область видимости</label>
            <div className="visibility-options">
              <label className={`visibility-option ${visibility === 'public' ? 'active' : ''}`}>
                <input
                  type="radio"
                  value="public"
                  checked={visibility === 'public'}
                  onChange={(e) => {
                    setVisibility('public');
                    setSelectedMembers([]);
                  }}
                />
                <div className="visibility-option-content">
                  <span className="visibility-icon">🌐</span>
                  <div className="visibility-text">
                    <span className="visibility-title">Общий</span>
                    <span className="visibility-subtitle">Показ у всех</span>
                  </div>
                </div>
              </label>
              <label className={`visibility-option ${visibility === 'private' ? 'active' : ''}`}>
                <input
                  type="radio"
                  value="private"
                  checked={visibility === 'private'}
                  onChange={(e) => setVisibility('private')}
                />
                <div className="visibility-option-content">
                  <span className="visibility-icon">🔒</span>
                  <div className="visibility-text">
                    <span className="visibility-title">Приватный</span>
                    <span className="visibility-subtitle">Для выбранных сотрудников</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
          {visibility === 'private' && (
            <div className="form-group">
              <label>Выберите сотрудников</label>
              {loadingUsers ? (
                <div className="users-loading">Загрузка пользователей...</div>
              ) : (
                <>
                  <div className="users-search-container">
                    <input
                      type="text"
                      className="users-search"
                      placeholder="Поиск по имени или email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="users-list">
                    {filteredUsers.length === 0 ? (
                      <div className="users-empty">
                        {searchQuery ? 'Ничего не найдено' : 'Нет доступных пользователей'}
                      </div>
                    ) : (
                      filteredUsers.map(user => (
                        <div
                          key={user.id}
                          className={`user-item ${selectedMembers.includes(user.id) ? 'selected' : ''}`}
                          onClick={() => toggleMember(user.id)}
                        >
                          <div className="user-check-indicator">
                            {selectedMembers.includes(user.id) && <span className="check-icon">✓</span>}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{user.username}</span>
                            {user.email && <span className="user-email">{user.email}</span>}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          {error && <div className="error">{error}</div>}
          <div className="modal-actions">
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBoardModal;

